import React from 'react';
import { useCurrencyOptional } from '../contexts/CurrencyContext';
import { USD_APPROX_NOTE } from '../lib/currency';
import { trackEvent } from './GoogleAnalytics';

/**
 * CURR-01: segmented KES/USD display-currency control.
 *
 * Renders NOTHING unless there is a usable, fresh rate behind it. A toggle that
 * silently does nothing is worse than no toggle, so the missing/stale-rate
 * fallback is "the control is not there", not "the control is there and inert".
 * That also covers the server render and the first client paint, where the rate
 * has not loaded yet.
 *
 * Buyer surfaces only. The header components that host it opt in explicitly;
 * agents never see it.
 */

const NOTE = 'USD is approximate, based on today\'s rate';

interface CurrencyToggleProps {
  className?: string;
  /** Show the short inline rate note beside the control (header use). */
  showNote?: boolean;
}

const CurrencyToggle: React.FC<CurrencyToggleProps> = ({ className = '', showNote = true }) => {
  const currencyCtx = useCurrencyOptional();

  // No provider, no rate, or a stale rate — render nothing at all.
  if (!currencyCtx || !currencyCtx.isRateUsable) return null;

  const { currency, setCurrency } = currencyCtx;

  const handleSelect = (next: 'KES' | 'USD') => {
    if (next === currency) return;

    setCurrency(next);

    // Only a real user switch is tracked. Restoring a saved USD preference on
    // load goes through the provider, not here, so it never fires this — which
    // keeps the metric a measure of intent rather than of returning visitors.
    if (next === 'USD') {
      trackEvent('currency_switch_usd', { currency: 'USD' });
    }
  };

  const base =
    'px-3 py-1.5 rounded-md text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-1';
  const active = 'bg-white text-gray-900 shadow-sm';
  const inactive = 'text-gray-500 hover:text-gray-700';

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="flex bg-gray-100 rounded-lg p-1" role="group" aria-label="Display currency">
        {(['KES', 'USD'] as const).map((code) => (
          <button
            key={code}
            type="button"
            onClick={() => handleSelect(code)}
            aria-pressed={currency === code}
            title={code === 'USD' ? NOTE : 'Show prices in Kenyan shillings'}
            className={`${base} ${currency === code ? active : inactive}`}
          >
            {code}
          </button>
        ))}
      </div>
      {showNote && currency === 'USD' && (
        <span className="hidden sm:inline text-xs text-gray-500 whitespace-nowrap">
          ≈ today&apos;s rate
        </span>
      )}
    </div>
  );
};

/**
 * The page-level note, for RESULT surfaces — a grid of cards where no single
 * figure is the focal one, so the statement is made once above the list.
 *
 * CURR-03 cut it to one line. It used to read "Prices are set in KES. USD
 * figures are approximate, based on today's rate.", which wrapped to two lines
 * on a phone and, on the detail page, put more muted type under the price than
 * the USD figure itself had — so the fine print out-weighed the number it was
 * annotating. The detail page no longer renders this at all; its figure carries
 * its own inline qualifier instead.
 *
 * Renders only in USD mode. Never says "quoted", "locked", or anything else
 * implying a held price.
 */
export const CurrencyRateNote: React.FC<{ className?: string }> = ({ className = '' }) => {
  const currencyCtx = useCurrencyOptional();

  if (!currencyCtx || !currencyCtx.isRateUsable || currencyCtx.currency !== 'USD') return null;

  return <p className={`text-xs text-gray-500 ${className}`}>{USD_APPROX_NOTE}</p>;
};

export default CurrencyToggle;
